import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ChevronDown, Instagram, Linkedin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n/index.js'

const LANGUAGES = [
  { code: 'pt', flag: 'flags/br.png', label: 'Português' },
  { code: 'en', flag: 'flags/us.png', label: 'English' },
  { code: 'es', flag: 'flags/es.png', label: 'Español' },
  { code: 'fr', flag: 'flags/fr.png', label: 'Français' },
]

const Header = () => {
  const { t } = useTranslation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null)
  const [currentLang, setCurrentLang] = useState(i18n.language || 'pt')
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (!isMobileMenuOpen) setOpenMobileSubmenu(null)
  }, [isMobileMenuOpen])

  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname, location.hash])

  const changeLanguage = (code) => {
    i18n.changeLanguage(code)
    setCurrentLang(code)
  }

  const scrollToSection = (hash) => {
    setIsMobileMenuOpen(false)
    setOpenMobileSubmenu(null)
    if (location.pathname !== '/') {
      navigate(`/#${hash}`)
    } else {
      const el = document.getElementById(hash)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const empresaSubmenu = [
    { name: t('nav.quemSomos'), hash: 'quem-somos' },
    { name: t('nav.presencaGlobal'), hash: 'presenca-global' },
    { name: t('nav.cases'), hash: 'cases' },
    { name: t('nav.certificacoes'), hash: 'certificacoes' },
  ]

  const isOnContact =
    location.pathname === '/contato' ||
    location.pathname === '/fale-conosco' ||
    location.pathname === '/solicite-cotacao' ||
    location.pathname === '/trabalhe-conosco'

  const textBase = 'text-white/85 hover:text-white'
  const textActive = 'text-white'

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, type: 'spring' }}
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-dark-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-0">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src={`${import.meta.env.BASE_URL}images/LogoFinal.png`}
              alt="AGS Global Logistics"
              className="h-36 w-auto object-contain transition-opacity duration-300 group-hover:opacity-80"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">

            {/* Empresa */}
            <div className="relative group">
              <button
                type="button"
                className={`font-medium transition-all duration-300 relative inline-flex items-center gap-1 ${textBase}`}
              >
                {t('nav.empresa')}
                <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary-600 transition-all duration-300 group-hover:w-full" />
              </button>
              <div className="absolute left-0 top-full pt-3 opacity-0 invisible -translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                <div className="min-w-[220px] rounded-xl border border-dark-100 bg-white shadow-xl p-2">
                  {empresaSubmenu.map((item) => (
                    <button
                      key={item.hash}
                      type="button"
                      onClick={() => scrollToSection(item.hash)}
                      className="w-full text-left block px-3 py-2 rounded-lg text-sm text-dark-700 hover:bg-dark-50 hover:text-primary-600 transition-colors"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Serviços */}
            <button
              type="button"
              onClick={() => scrollToSection('servicos')}
              className={`font-medium transition-all duration-300 relative group ${textBase}`}
            >
              {t('nav.servicos')}
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary-600 transition-all duration-300 group-hover:w-full" />
            </button>

            {/* Divisões */}
            <button
              type="button"
              onClick={() => scrollToSection('divisoes')}
              className={`font-medium transition-all duration-300 relative group ${textBase}`}
            >
              {t('nav.divisoes')}
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary-600 transition-all duration-300 group-hover:w-full" />
            </button>

            {/* Contato */}
            <Link
              to="/contato"
              className={`font-medium transition-all duration-300 relative group ${isOnContact ? textActive : textBase}`}
            >
              {t('nav.contato')}
              <span className={`absolute bottom-0 left-0 h-0.5 bg-primary-600 transition-all duration-300 group-hover:w-full ${isOnContact ? 'w-full' : 'w-0'}`} />
            </Link>
          </nav>

          {/* Lado direito: Seletor de Idioma + Redes Sociais + Pantera */}
          <div className="hidden lg:flex items-center gap-2">

            {/* Seletor de Idioma — menu suspenso */}
            <div className="relative group mr-2">
              {/* Trigger */}
              <button
                type="button"
                className="font-medium transition-all duration-300 relative inline-flex items-center gap-1.5 text-white/85 hover:text-white"
              >
                {(() => {
                  const cur = LANGUAGES.find((l) => l.code === currentLang)
                  return cur ? (
                    <>
                      <img
                        src={`${import.meta.env.BASE_URL}images/${cur.flag}`}
                        alt={cur.label}
                        className="w-6 h-4 object-cover rounded-sm shadow"
                      />
                      <span className="text-sm font-semibold hidden xl:inline">{cur.label}</span>
                    </>
                  ) : null
                })()}
                <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary-600 transition-all duration-300 group-hover:w-full" />
              </button>
              {/* Dropdown — mesmo padrão do menu Empresa */}
              <div className="absolute right-0 top-full pt-3 opacity-0 invisible -translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                <div className="min-w-[180px] rounded-xl border border-dark-100 bg-white shadow-xl p-2">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        currentLang === lang.code
                          ? 'bg-[#0C2D54]/10 text-[#0C2D54] font-bold'
                          : 'text-dark-700 hover:bg-dark-50 hover:text-primary-600'
                      }`}
                    >
                      <img
                        src={`${import.meta.env.BASE_URL}images/${lang.flag}`}
                        alt={lang.label}
                        className="w-7 h-5 object-cover rounded-sm shadow-sm flex-shrink-0"
                      />
                      <span className="font-semibold">{lang.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <a
              href="https://www.instagram.com/agsgloballogistics/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram AGS"
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all duration-200"
            >
              <Instagram size={20} />
            </a>
            <a
              href="https://www.linkedin.com/company/agsgloballogistics/posts/?feedView=all"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn AGS"
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all duration-200"
            >
              <Linkedin size={20} />
            </a>
            <img
              src={`${import.meta.env.BASE_URL}images/pantera.png`}
              alt="Pantera AGS"
              className="h-20 w-auto object-contain"
            />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden mt-4 py-4 bg-dark-900/95 backdrop-blur-md rounded-lg shadow-xl overflow-hidden border border-white/10"
            >
              <nav className="flex flex-col space-y-2 px-2">

                {/* Empresa */}
                <div>
                  <button
                    type="button"
                    onClick={() => setOpenMobileSubmenu((p) => (p === 'empresa' ? null : 'empresa'))}
                    className="w-full px-4 py-3 rounded-lg text-white/85 hover:bg-white/10 transition-colors flex items-center justify-between font-medium"
                  >
                    <span>{t('nav.empresa')}</span>
                    <ChevronDown size={18} className={`transition-transform ${openMobileSubmenu === 'empresa' ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {openMobileSubmenu === 'empresa' && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                        <div className="ml-4 pl-3 border-l border-white/20 space-y-1 mt-1">
                          {empresaSubmenu.map((item) => (
                            <button
                              key={item.hash}
                              type="button"
                              onClick={() => scrollToSection(item.hash)}
                              className="w-full text-left block px-3 py-2 rounded-lg text-sm text-white/75 hover:bg-white/10 hover:text-white transition-colors"
                            >
                              {item.name}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Serviços */}
                <button
                  type="button"
                  onClick={() => scrollToSection('servicos')}
                  className="text-left px-4 py-3 rounded-lg text-white/85 hover:bg-white/10 hover:text-white transition-colors font-medium"
                >
                  {t('nav.servicos')}
                </button>

                {/* Divisões */}
                <button
                  type="button"
                  onClick={() => scrollToSection('divisoes')}
                  className="text-left px-4 py-3 rounded-lg text-white/85 hover:bg-white/10 hover:text-white transition-colors font-medium"
                >
                  {t('nav.divisoes')}
                </button>

                {/* Contato */}
                <Link
                  to="/contato"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-left px-4 py-3 rounded-lg text-white/85 hover:bg-white/10 hover:text-white transition-colors font-medium"
                >
                  {t('nav.contato')}
                </Link>

                {/* Seletor de Idioma Mobile */}
                <div className="px-2 pt-1 pb-2">
                  <div className="rounded-lg overflow-hidden border border-white/10">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold transition-colors ${
                          currentLang === lang.code
                            ? 'bg-white/15 text-white'
                            : 'text-white/70 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <img
                          src={`${import.meta.env.BASE_URL}images/${lang.flag}`}
                          alt={lang.label}
                          className="w-7 h-5 object-cover rounded-sm shadow flex-shrink-0"
                        />
                        <span>{lang.label}</span>
                        {currentLang === lang.code && (
                          <span className="ml-auto w-2 h-2 rounded-full bg-[#38bdf8]" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  )
}

export default Header
